import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, UploadSimple, FileText, Check, Warning, DownloadSimple } from '@phosphor-icons/react';
import { createItem } from '../../utils/api';
import { toast } from 'sonner';

const BulkImportModal = ({ isOpen, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, success: 0, failed: 0 });
  const [errors, setErrors] = useState([]);
  const fileInputRef = useRef(null);

  const sampleCSV = `name,description,purchase_rate,selling_rate,quantity,category_id
Gold Necklace Set,Beautiful gold plated necklace,500,1200,10,1
Pearl Earrings,Elegant pearl drop earrings,200,450,25,2
Diamond Bracelet,Sparkling diamond bracelet,800,1800,5,3`;

  const downloadSampleCSV = () => {
    const blob = new Blob([sampleCSV], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_items_import.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const parseCSV = (text) => {
    const lines = text.trim().split('\n');
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    
    const requiredHeaders = ['name', 'purchase_rate', 'selling_rate'];
    const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
    
    if (missingHeaders.length > 0) {
      toast.error(`Missing required columns: ${missingHeaders.join(', ')}`);
      return [];
    }

    const data = [];
    for (let i = 1; i < lines.length; i++) {
      if (!lines[i].trim()) continue;
      
      const values = lines[i].split(',').map(v => v.trim());
      const item = {};
      
      headers.forEach((header, index) => {
        item[header] = values[index] || '';
      });
      
      // Validate required fields
      if (!item.name || !item.purchase_rate || !item.selling_rate) {
        continue;
      }
      
      data.push({
        name: item.name,
        description: item.description || '',
        purchase_rate: parseFloat(item.purchase_rate) || 0,
        selling_rate: parseFloat(item.selling_rate) || 0,
        quantity: parseInt(item.quantity) || 0,
        category_id: item.category_id ? parseInt(item.category_id) : null,
        row: i + 1
      });
    }
    
    return data;
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    
    if (!selectedFile.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    
    setFile(selectedFile);
    const reader = new FileReader();
    reader.onload = (event) => {
      const data = parseCSV(event.target.result);
      setParsedData(data);
      setErrors([]);
    };
    reader.readAsText(selectedFile);
  };

  const handleImport = async () => {
    if (parsedData.length === 0) {
      toast.error('No valid items to import');
      return;
    }

    setImporting(true);
    setProgress({ current: 0, total: parsedData.length, success: 0, failed: 0 });
    const importErrors = [];

    for (let i = 0; i < parsedData.length; i++) {
      const item = parsedData[i];
      try {
        const formData = new FormData();
        formData.append('name', item.name);
        formData.append('description', item.description);
        formData.append('purchase_rate', item.purchase_rate);
        formData.append('selling_rate', item.selling_rate);
        formData.append('quantity', item.quantity);
        if (item.category_id) {
          formData.append('category_id', item.category_id);
        }

        await createItem(formData);
        setProgress(prev => ({ ...prev, current: i + 1, success: prev.success + 1 }));
      } catch (error) {
        importErrors.push({ row: item.row, name: item.name, error: error.response?.data?.detail || 'Failed to import' });
        setProgress(prev => ({ ...prev, current: i + 1, failed: prev.failed + 1 }));
      }
    }

    setErrors(importErrors);
    setImporting(false);

    if (importErrors.length === 0) {
      toast.success(`Successfully imported ${parsedData.length} items`);
      onSuccess?.();
      handleClose();
    } else {
      toast.warning(`Imported ${progress.success} items, ${importErrors.length} failed`);
    }
  };

  const handleClose = () => {
    setFile(null);
    setParsedData([]);
    setErrors([]);
    setProgress({ current: 0, total: 0, success: 0, failed: 0 });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-sm"
      >
        <div className="flex items-center justify-between p-4 border-b border-border-default sticky top-0 bg-white z-10">
          <h3 className="font-display text-xl text-secondary">Bulk Import Items</h3>
          <button onClick={handleClose} disabled={importing}>
            <X size={24} className="text-text-muted hover:text-secondary" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Instructions */}
          <div className="p-4 bg-blue-50 border border-blue-100 rounded">
            <h4 className="font-body text-sm font-medium text-blue-800 mb-2">CSV Format Instructions</h4>
            <ul className="font-body text-xs text-blue-700 space-y-1">
              <li>• Required columns: <strong>name, purchase_rate, selling_rate</strong></li>
              <li>• Optional columns: description, quantity, category_id</li>
              <li>• First row must be column headers</li>
            </ul>
            <button
              onClick={downloadSampleCSV}
              className="mt-3 flex items-center gap-2 text-blue-600 hover:text-blue-800 font-body text-sm"
            >
              <DownloadSimple size={16} />
              Download Sample CSV
            </button>
          </div>

          {/* File Upload */}
          <div
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded p-8 text-center cursor-pointer transition-colors ${
              file ? 'border-primary bg-primary/5' : 'border-gray-300 hover:border-primary'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="hidden"
              disabled={importing}
            />
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <FileText size={32} className="text-primary" />
                <div className="text-left">
                  <p className="font-body text-sm text-secondary font-medium">{file.name}</p>
                  <p className="font-body text-xs text-text-muted">{parsedData.length} items ready to import</p>
                </div>
              </div>
            ) : (
              <>
                <UploadSimple size={40} className="text-text-muted mx-auto mb-3" />
                <p className="font-body text-sm text-text-secondary">Click to upload CSV file</p>
                <p className="font-body text-xs text-text-muted mt-1">or drag and drop</p>
              </>
            )}
          </div>

          {/* Preview */}
          {parsedData.length > 0 && !importing && (
            <div>
              <h4 className="font-body text-sm font-medium text-secondary mb-2">Preview (First 5 items)</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-background-subtle">
                      <th className="text-left py-2 px-3 font-body text-xs text-text-secondary">Name</th>
                      <th className="text-right py-2 px-3 font-body text-xs text-text-secondary">Purchase</th>
                      <th className="text-right py-2 px-3 font-body text-xs text-text-secondary">Selling</th>
                      <th className="text-center py-2 px-3 font-body text-xs text-text-secondary">Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedData.slice(0, 5).map((item, index) => (
                      <tr key={index} className="border-b border-border-default">
                        <td className="py-2 px-3 font-body text-secondary">{item.name}</td>
                        <td className="py-2 px-3 font-body text-text-secondary text-right">₹{item.purchase_rate}</td>
                        <td className="py-2 px-3 font-body text-text-secondary text-right">₹{item.selling_rate}</td>
                        <td className="py-2 px-3 font-body text-text-secondary text-center">{item.quantity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {parsedData.length > 5 && (
                <p className="font-body text-xs text-text-muted mt-2 text-center">
                  ... and {parsedData.length - 5} more items
                </p>
              )}
            </div>
          )}

          {/* Progress */}
          {importing && (
            <div className="space-y-3">
              <div className="flex justify-between font-body text-sm">
                <span className="text-text-secondary">Importing...</span>
                <span className="text-secondary">{progress.current} / {progress.total}</span>
              </div>
              <div className="h-2 bg-gray-200 rounded overflow-hidden">
                <div 
                  className="h-full bg-primary transition-all duration-300"
                  style={{ width: `${(progress.current / progress.total) * 100}%` }}
                />
              </div>
              <div className="flex gap-4 font-body text-xs">
                <span className="text-green-600 flex items-center gap-1">
                  <Check size={14} /> {progress.success} success
                </span>
                <span className="text-red-500 flex items-center gap-1">
                  <Warning size={14} /> {progress.failed} failed
                </span>
              </div>
            </div>
          )}

          {/* Errors */}
          {errors.length > 0 && (
            <div className="p-4 bg-red-50 border border-red-100 rounded">
              <h4 className="font-body text-sm font-medium text-red-800 mb-2">Import Errors</h4>
              <ul className="space-y-1 max-h-32 overflow-y-auto">
                {errors.map((err, index) => (
                  <li key={index} className="font-body text-xs text-red-700">
                    Row {err.row} ({err.name}): {err.error}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={importing}
              className="btn-secondary flex-1 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={importing || parsedData.length === 0}
              data-testid="start-import-btn"
              className="btn-primary flex-1 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {importing ? 'Importing...' : `Import ${parsedData.length} Items`}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default BulkImportModal;
